import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAssetUrl } from '../lib/hooks';
import { Typography } from '../typography';

import styles from './bottom-sheet.scss';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    open: boolean;
    hero?: boolean;
    vertical?: boolean;
    image?: string;
    title?: string;
    description?: string;
    buttons?: React.ReactNode;
    header?: React.ReactElement;
    dataTestId?: string;
    onShadowClick?: () => void;
    onSwipeDown?: () => void;
    openHeight?: number;
    minOpen?: number;
  };

const COMPONENT_ID = 'modulor-bottom-sheet';

const ANIMATION_DURATION = 250;

const SHADOW_OPACITY = '0.4';

export const BottomSheet: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      open,
      hero,
      image,
      title,
      description,
      onShadowClick,
      vertical,
      buttons,
      header,
      children,
      minOpen,
      openHeight,
      onSwipeDown,
      dataTestId = COMPONENT_ID,
      className,
      ...rest
    },
    ref,
  ) => {
    const headerRef = useRef(null);
    const swiperRef = useRef(null);
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const shadowRef = useRef(null);
    const buttonsRef = useRef(null);
    const bottomSheetRef = useRef(null);

    const [transformStart, setTransformStart] = useState<number>(0);

    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isExpanding, setIsExpanding] = useState<boolean>(false);
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const [isCollapsing, setIsCollapsing] = useState<boolean>(false);

    const [maxHiddenHeight, setMaxHiddenHeight] = useState<number>(0);
    const [startY, setStartY] = useState<number>(0);

    const imageUrl = useAssetUrl(image);

    const headerBackground = hero && imageUrl ? { backgroundImage: `url('${imageUrl}')` } : {};

    const isiOS = /iPhone|iPad/i.test(navigator.userAgent);

    const getTransform = (): number => {
      if (typeof window === 'undefined') return 0;

      if (containerRef.current) {
        return new DOMMatrix(window.getComputedStyle(containerRef.current, null).webkitTransform).f;
      }
    };

    const handleDragStart = () => {
      setIsDragging(true);
      setTransformStart(getTransform);
    };

    const fixedOpen = useMemo((): number | null => {
      const isOpenHeightValid = openHeight >= 20 && openHeight <= 100;
      return isOpenHeightValid ? Number((openHeight / 100).toFixed(1)) : null;
    }, [openHeight]);

    const initialOpen = useMemo((): number => {
      const isMinOpenValid = minOpen >= 20 && minOpen <= 100;
      return isMinOpenValid ? Number((minOpen / 100).toFixed(1)) : 1;
    }, [minOpen]);

    const getHeaderHeight = (): number | undefined => {
      const headerElement = headerRef.current;

      if (headerElement && headerElement.clientHeight) {
        const headerDivider = headerElement?.hasAttribute('transparent') ? 0 : 2;
        const headerHeight = headerElement?.clientHeight + headerDivider;
        return headerHeight > headerElement?.clientHeight
          ? headerHeight
          : headerElement.clientHeight;
      }
    };

    const getHeights = useCallback(() => {
      const footerHeight = buttonsRef.current ? buttonsRef.current?.clientHeight : 16;

      return {
        swiperHeight: swiperRef.current?.clientHeight,
        headerHeight: getHeaderHeight(),
        contentHeight: contentRef.current?.clientHeight,
        footerHeight,
      };
    }, []);

    const disableScroll = useCallback(() => {
      if (typeof document === 'undefined') return;

      if (isiOS) {
        document.documentElement.style.overflowY = 'hidden';
        document.documentElement.style.bottom = '0';
        document.documentElement.style.maxHeight = '100%';
        document.documentElement.style.maxWidth = '100%';
      } else {
        document.body.style.overflowY = 'hidden';
        document.body.style.height = '100%';
        document.body.style.maxHeight = '100%';
        document.body.style.maxWidth = '100%';
      }
    }, [isiOS]);

    const enableScroll = useCallback(() => {
      if (typeof document === 'undefined') return;

      document.body.style.removeProperty('overflow-y');
      document.body.style.removeProperty('height');
      document.body.style.removeProperty('max-height');
      document.body.style.removeProperty('max-width');

      document.documentElement.style.removeProperty('overflow-y');
      document.documentElement.style.removeProperty('bottom');
      document.documentElement.style.removeProperty('max-height');
      document.documentElement.style.removeProperty('max-width');
    }, []);

    const checkForScroll = () => {
      buttonsRef.current.classList.toggle(
        styles['has-border'],
        contentRef.current.scrollHeight > contentRef.current.clientHeight,
      );
    };

    const setDesktopMaxHeight = useCallback(() => {
      const { headerHeight, footerHeight, swiperHeight } = getHeights();

      const maxBottomSheetHeight = parseInt(getComputedStyle(bottomSheetRef.current).maxHeight, 10);

      const maxContentHeight = maxBottomSheetHeight + swiperHeight - headerHeight - footerHeight;

      contentRef.current.style.maxHeight = `${maxContentHeight}px`;
    }, [getHeights]);

    const setHeight = useCallback(() => {
      if (typeof window === 'undefined') return;

      const { swiperHeight, footerHeight } = getHeights();
      const maxPossibleHeight = window.innerHeight - swiperHeight;
      const maxMobileHeight = fixedOpen ? maxPossibleHeight * Number(fixedOpen) : maxPossibleHeight;

      if (containerRef.current) containerRef.current.style.maxHeight = `${maxMobileHeight}px`;

      if (bottomSheetRef.current) bottomSheetRef.current.style.maxHeight = `${maxMobileHeight}px`;

      if (contentRef.current) contentRef.current.style.paddingBottom = `${footerHeight}px`;

      if (fixedOpen && bottomSheetRef.current) {
        bottomSheetRef.current.style.height = `${maxMobileHeight}px`;
      }

      setMaxHiddenHeight(
        Math.max(0, maxMobileHeight - swiperHeight - maxPossibleHeight * initialOpen),
      );
    }, [fixedOpen, getHeights, initialOpen]);

    const isCollapsed = useCallback(() => {
      return Math.round(getTransform()) === Math.round(maxHiddenHeight);
    }, [maxHiddenHeight]);

    const animateClosing = useCallback(() => {
      shadowRef.current?.classList.add(styles.closing);

      if (bottomSheetRef.current) {
        const animation = bottomSheetRef.current.animate(
          [
            { transform: 'translateY(0)', opacity: 1 },
            { transform: 'translateY(100%)', opacity: 0 },
          ],
          { duration: ANIMATION_DURATION },
        );

        animation.onfinish = () => {
          bottomSheetRef.current.style.display = 'none';
          containerRef.current.style.display = 'none';
          onSwipeDown();
        };
      }
    }, [onSwipeDown]);

    const handleAnimationEnd = useCallback(
      (closing = false) => {
        containerRef.current.style.transform = `translateY(${maxHiddenHeight}px)`;
        bottomSheetRef.current.style.transform = `translateY(${maxHiddenHeight}px)`;
        shadowRef.current.classList.remove(styles['to-bottom']);
        if (closing) {
          animateClosing();
        }
      },
      [animateClosing, maxHiddenHeight],
    );

    const animateToBottom = useCallback(
      (closing = false) => {
        shadowRef.current?.classList.add(styles['to-bottom']);

        if (containerRef.current) {
          const animation = containerRef.current.animate(
            [
              { transform: `translateY(${getTransform()}px)` },
              { transform: `translateY(${maxHiddenHeight}px)` },
            ],
            { duration: ANIMATION_DURATION },
          );
          animation.onfinish = () => handleAnimationEnd(closing);
        }
      },
      [handleAnimationEnd, maxHiddenHeight],
    );

    const handleTouchStart = (e) => {
      setStartY(e.touches[0].clientY);
    };

    const handleDismissStart = (e) => {
      const isContentTouch = e.currentTarget === contentRef.current;
      const isDismissAllowed = !(isContentTouch && contentRef.current.scrollTop);

      setIsDragging(isDismissAllowed);
    };

    const resetStart = () => {
      setStartY(0);
      setIsDragging(false);
    };

    const handleTouchEnd = useCallback(
      (e) => {
        if (!isDragging) return;

        const endY = e.changedTouches[0].clientY;
        const shift = startY - endY;
        const isSwipeUp = shift > 0;
        const isSwipeDown = shift < 0;
        const isEnoughLongSwipe = Math.abs(shift) > 1 && Math.abs(shift) >= maxHiddenHeight / 2;

        if (isExpanding && isSwipeUp) return;

        const isExpandingOnTouch =
          (isDragging && isSwipeUp && isEnoughLongSwipe) ||
          (isDragging && isSwipeDown && !isEnoughLongSwipe && !isCollapsed()) ||
          (!isDragging && isSwipeUp);
        const isClosingOnTouch = isDragging && isSwipeDown && isEnoughLongSwipe && isCollapsed();
        const isCollapsingOnTouch = isDragging && isSwipeDown && isEnoughLongSwipe;

        setIsExpanding(isExpandingOnTouch);

        setIsClosing(isClosingOnTouch);

        setIsCollapsing(isCollapsingOnTouch);
      },
      [isCollapsed, isDragging, isExpanding, maxHiddenHeight, startY],
    );

    const setShadowOpacity = useCallback(
      (hiddenHeight: number) => {
        const minOpacity = Number(SHADOW_OPACITY);
        const opacityReserve = 1 - minOpacity;
        const opacity =
          minOpacity + opacityReserve * ((100 - (hiddenHeight * 100) / maxHiddenHeight) / 100);
        shadowRef.current.style.opacity = `${opacity}`;
      },
      [maxHiddenHeight],
    );

    const handleTouchMove = useCallback(
      (e) => {
        if (isDragging) {
          const dragY = e.touches[0].clientY;
          const shift = dragY - startY;
          const newHiddenHeight = transformStart + shift;

          if (newHiddenHeight > 0 && newHiddenHeight < maxHiddenHeight) {
            setShadowOpacity(newHiddenHeight);
            bottomSheetRef.current.classList.toggle(styles.expanded, newHiddenHeight < 3);
            containerRef.current.style.transform = `translateY(${newHiddenHeight}px)`;
          }
        }
      },
      [isDragging, maxHiddenHeight, setShadowOpacity, startY, transformStart],
    );

    useEffect(() => {
      if (maxHiddenHeight !== 0 && containerRef.current) {
        containerRef.current.style.transform = `translateY(${maxHiddenHeight}px)`;
      }
    }, [maxHiddenHeight]);

    useEffect(() => {
      if (open) {
        requestAnimationFrame(() => {
          setHeight();
          setDesktopMaxHeight();
          checkForScroll();
        });
        disableScroll();
      } else {
        setIsExpanding(false);
        setIsClosing(false);
        setIsCollapsing(false);
        setMaxHiddenHeight(0);
        enableScroll();
      }

      return () => {
        enableScroll();
      };
    }, [
      children,
      header,
      buttons,
      open,
      disableScroll,
      setHeight,
      setDesktopMaxHeight,
      enableScroll,
    ]);

    useEffect(() => {
      if (isExpanding) {
        setDesktopMaxHeight();
      }
    }, [isExpanding, setDesktopMaxHeight]);

    useEffect(() => {
      if ((!minOpen || Number(minOpen) === 100) && open) {
        setIsExpanding(true);
        resetStart();
      }
    }, [minOpen, open]);

    useEffect(() => {
      if (isCollapsing) {
        animateToBottom();
        resetStart();
      }
    }, [animateToBottom, isCollapsing]);

    useEffect(() => {
      if (isClosing) {
        animateToBottom(true);
      }
    }, [animateToBottom, isClosing]);

    return open ? (
      <div
        className={classNames(className, styles['bottom-sheet-wrapper'])}
        data-component-name={COMPONENT_ID}
        data-testid={dataTestId}
        data-id={dataTestId}
        ref={ref}
        {...rest}>
        <div
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          onTouchStart={handleTouchStart}
          ref={bottomSheetRef}
          className={classNames(styles['bottom-sheet'], {
            [styles.expanded]: isExpanding,
            [styles.open]: open,
          })}>
          <div className={styles.container} ref={containerRef}>
            <span
              className={styles.swiper}
              onTouchStart={handleDragStart}
              ref={swiperRef}
              data-testid={`${dataTestId}-swiper`}
            />
            <div
              style={headerBackground}
              onTouchStart={handleDismissStart}
              data-testid={`${dataTestId}-header`}
              className={classNames(styles.header, {
                [styles['with-image']]: image,
                [styles.hero]: image && hero,
                [styles.hidden]: header === null,
              })}>
              <div ref={headerRef}>{header}</div>
              {image && !hero ? (
                <div
                  className={styles.image}
                  style={{ backgroundImage: `url('${imageUrl}')` }}
                  data-testid={`${dataTestId}-header-image`}
                />
              ) : null}
            </div>
            <div
              onTouchStart={handleDismissStart}
              className={styles.content}
              ref={contentRef}
              data-testid={`${dataTestId}-content`}>
              {title && (
                <Typography
                  className={styles.title}
                  variant="large-title-semibold"
                  dataTestId={`${dataTestId}-title`}>
                  {title}
                </Typography>
              )}
              {description && (
                <Typography
                  className={styles.description}
                  variant="body-regular"
                  dataTestId={`${dataTestId}-description`}>
                  {description}
                </Typography>
              )}
              {children}
            </div>
          </div>
          <div
            ref={buttonsRef}
            data-testid={`${dataTestId}-buttons`}
            className={classNames(styles['button-docked'], {
              [styles.vertical]: vertical,
            })}>
            {buttons}
          </div>
        </div>
        <div
          className={styles.shadow}
          ref={shadowRef}
          onClick={onShadowClick}
          data-testid={`${dataTestId}-shadow`}
        />
      </div>
    ) : null;
  },
);

BottomSheet.displayName = 'BottomSheet';
